import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import './i18n'; // must import before App so translations are ready
import './dreamy.css'; // global motion layer (animations, micro-interactions)
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { ConfirmProvider } from './components/ConfirmModal';
import { ThemeProvider } from './context/ThemeContext';
import { Capacitor } from '@capacitor/core';

// ── Guard against third-party DOM mutation (page translators / extensions) ────
// Microsoft Edge & Chrome "Translate this page", Grammarly, password managers,
// etc. rewrap/move the text nodes inside elements React manages. When React
// then swaps that text (e.g. a button label changing on submit), the node it
// expects is no longer a child and removeChild/insertBefore throw a
// NotFoundError that crashes the whole tree via the ErrorBoundary. These guards
// make the two calls degrade gracefully when the node isn't actually a child —
// only the pathological case is affected; normal React updates call through
// unchanged. This is the long-standing community fix for facebook/react#11538.
if (typeof Node === 'function' && Node.prototype) {
  const origRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) return child;
    return origRemoveChild.apply(this, arguments);
  };
  const origInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) return newNode;
    return origInsertBefore.apply(this, arguments);
  };
}

if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn:              process.env.REACT_APP_SENTRY_DSN,
    environment:      process.env.NODE_ENV || 'production',
    tracesSampleRate: 0.2,  // 20% of page loads traced
    replaysSessionSampleRate: 0,       // no session replays (saves quota)
    replaysOnErrorSampleRate: 1.0,     // replay on every error
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Don't send errors from browser extensions, localhost, or in-app-browser
    // injected scripts (Facebook/Instagram/TikTok inject their own JS that
    // throws on page teardown — not our code, not our bug)
    denyUrls: [/localhost/, /127\.0\.0\.1/, /iabjs:\/\//, /^fbredirect:/, /instagram\.com\/.*iab/],
    // Noise from in-app-browser telemetry (Facebook/Instagram/TikTok inject
    // their own JS to log load timing & clicks back to the native app; it
    // throws on teardown or when the native bridge is missing — never our bug)
    ignoreErrors: [
      // Android FB in-app browser
      'enableButtonsClickedMetaDataLogging',
      'Java object is gone',
      'Java bridge method invocation error',
      /navigation_performance_logger/,
      'sendBeforeUnloadMessage',
      // iOS FB/IG in-app browser (WKWebView native bridge)
      'window.webkit.messageHandlers',
      'webkit.messageHandlers',
      'sendDataToNative',
      'processLargestContentfulPaintEvent',
      // Page-translation / extension DOM mutations (Edge/Chrome Translate,
      // Grammarly, etc.) — guarded in index.js so they no longer crash the app
      // IndexedDB torn down under a library (Firebase's databases) when the
      // browser clears site data mid-session — crawlers do it between pages.
      // Our own code never uses IndexedDB, so these are never ours.
      /Connection is closing because of/,
      /Database deleted by request of the user/,
      "Failed to execute 'removeChild' on 'Node'",
      "Failed to execute 'insertBefore' on 'Node'",
      'The node to be removed is not a child of this node',
      'The node before which the new node is to be inserted is not a child of this node',
    ],
    beforeSend(event) {
      // Don't report 401/403 — expected for unauthenticated users
      const status = event.extra?.status || event.tags?.status;
      if (status === 401 || status === 403) return null;

      const exc = event.exception?.values?.[0];

      // Drop anything whose stack originates from an injected in-app-browser
      // script (Facebook/IG/TikTok), even if the message differs
      const frames = exc?.stacktrace?.frames || [];
      if (frames.some(f => f.filename && /iabjs:\/\/|in.?app.?browser/i.test(f.filename))) {
        return null;
      }

      // Drop native-bridge errors by message — no legitimate web code touches
      // window.webkit.messageHandlers or the Android native bridge
      const msg = exc?.value || event.message || '';
      if (/webkit\.messageHandlers|messageHandlers|sendDataToNative|Java object is gone|native bridge/i.test(msg)) {
        return null;
      }

      return event;
    },
  });
}

// Mark the document as the native app BEFORE first paint so the global
// safe-area CSS (notch / home-indicator insets) is in effect from the very
// first render — not a frame later once React mounts NativeBridge.
try {
  if (Capacitor?.isNativePlatform?.()) document.documentElement.classList.add('is-native');
} catch (_) { /* never let this block boot */ }

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <ThemeProvider>
        <ErrorBoundary>
          <ConfirmProvider>
            <App />
          </ConfirmProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </HelmetProvider>
  </React.StrictMode>
);
